class CurrencySelector {
  constructor(formElement) {
    this.form = formElement;
    this.toggle = this.form.querySelector('[data-disclosure-toggle]');
    this.list = this.form.querySelector('[data-disclosure-list]');
    this.input = this.form.querySelector('[data-disclosure-input]');
    this.options = this.form.querySelectorAll('[data-disclosure-option]');
    this.isOpen = false;

    this.init();
  }

  init() {
    if (!this.toggle || !this.list) return;

    // Bind event listeners
    this.toggle.addEventListener('click', this.handleToggle.bind(this));
    this.toggle.addEventListener('keydown', this.handleToggleKeydown.bind(this));

    this.options.forEach(option => {
      option.addEventListener('click', this.handleOptionClick.bind(this));
      option.addEventListener('keydown', this.handleOptionKeydown.bind(this));
    });

    // Close on outside click
    document.addEventListener('click', this.handleOutsideClick.bind(this));

    // Close on escape key
    document.addEventListener('keydown', this.handleEscape.bind(this));
  }

  handleToggle(e) {
    e.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  handleToggleKeydown(e) {
    switch(e.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
        e.preventDefault();
        this.open();
        this.focusFirstOption();
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.open();
        this.focusLastOption();
        break;
    }
  }

  handleOptionClick(e) {
    e.preventDefault();
    const option = e.currentTarget;
    const value = option.dataset.value;
    const redirectUrl = option.dataset.redirectUrl;

    // If there's a redirect URL, navigate there
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }

    // Otherwise, update the form and submit
    this.selectOption(option, value);
  }

  handleOptionKeydown(e) {
    switch(e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.handleOptionClick(e);
        break;
      case 'ArrowDown':
        e.preventDefault();
        this.focusNextOption(e.target);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.focusPreviousOption(e.target);
        break;
      case 'Home':
        e.preventDefault();
        this.focusFirstOption();
        break;
      case 'End':
        e.preventDefault();
        this.focusLastOption();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  handleOutsideClick(e) {
    if (this.isOpen && !this.form.contains(e.target)) {
      this.close();
    }
  }

  handleEscape(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
      this.toggle.focus();
    }
  }

  open() {
    this.isOpen = true;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.list.style.display = 'block';
    this.form.classList.add('disclosure--open');
  }

  close() {
    this.isOpen = false;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.list.style.display = 'none';
    this.form.classList.remove('disclosure--open');
  }

  selectOption(option, value) {
    // Update hidden input
    if (this.input) {
      this.input.value = value;
    }

    // Remove current selection
    this.options.forEach(opt => {
      opt.removeAttribute('aria-current');
      opt.closest('.disclosure-list__item').classList.remove('disclosure-list__item--current');
    });

    // Mark new selection
    option.setAttribute('aria-current', 'true');
    option.closest('.disclosure-list__item').classList.add('disclosure-list__item--current');

    // Update toggle display
    const flag = option.querySelector('.currency-flag').cloneNode(true);
    const label = option.querySelector('.currency-options__label').cloneNode(true);

    const toggleFlag = this.toggle.querySelector('.currency-flag');
    const toggleLabel = this.toggle.querySelector('.currency-options__label');

    if (toggleFlag) toggleFlag.replaceWith(flag);
    if (toggleLabel) toggleLabel.replaceWith(label);

    this.close();

    // Submit the form to update localization
    this.form.submit();
  }

  focusFirstOption() {
    if (this.options.length > 0) {
      this.options[0].focus();
    }
  }

  focusLastOption() {
    if (this.options.length > 0) {
      this.options[this.options.length - 1].focus();
    }
  }

  focusNextOption(currentOption) {
    const currentIndex = Array.from(this.options).indexOf(currentOption);
    const nextIndex = (currentIndex + 1) % this.options.length;
    this.options[nextIndex].focus();
  }

  focusPreviousOption(currentOption) {
    const currentIndex = Array.from(this.options).indexOf(currentOption);
    const prevIndex = currentIndex === 0 ? this.options.length - 1 : currentIndex - 1;
    this.options[prevIndex].focus();
  }
}

// Initialize all currency selectors on the page
function initializeCurrencySelectors() {
  const forms = document.querySelectorAll('[data-disclosure-form]');
  forms.forEach(form => new CurrencySelector(form));
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCurrencySelectors);
} else {
  initializeCurrencySelectors();
}

// Also initialize any new selectors added dynamically
const observer = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        // Check if the added node is a currency selector
        if (node.hasAttribute && node.hasAttribute('data-disclosure-form')) {
          new CurrencySelector(node);
        }
        // Check if the added node contains currency selectors
        const selectors = node.querySelectorAll ? node.querySelectorAll('[data-disclosure-form]') : [];
        selectors.forEach(form => new CurrencySelector(form));
      }
    });
  });
});

observer.observe(document.body, { childList: true, subtree: true });